'use client'
import React, { useState } from 'react'
import styled, { css } from 'styled-components'
import {
    // La casa es la convención universal de "inicio", así que se la queda Home.
    // Manager (la biblioteca de configs guardadas) usa un ícono de biblioteca:
    // tenía la casa y quedaban dos casitas idénticas pegadas, indistinguibles
    // sobre todo con el sidebar colapsado en el editor.
    Home as HomeIcon,
    LibraryBooks as ManagerIcon,
    MenuBook as WikiIcon,
    Favorite as BlogIcon,
    History as UpdatesIcon,
    GpsFixed as EditorIcon,
    Translate as LanguageIcon,
    AutoFixHigh,
    Tune as SettingsIcon,
    AccountCircle as LoginIcon
} from '@mui/icons-material'
import { Tooltip } from '@mui/material'
import { useAuth } from '@/contexts/AuthContext'
import { useLanguage } from '@/contexts/LanguageContext'
import { usePro } from '@/contexts/ProContext'
import { getBridge } from '@/utils/bridge'
import AnimatedLogo from './AnimatedLogo'
import AdSlot from '@/components/Ads/AdSlot'

const LogoSection = styled.div`
    margin-bottom: ${props => props.$expanded ? '4px' : '36px'};
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    min-height: 50px;
    overflow: hidden;
    transition: all 0.3s ease;
    padding-top: ${props => props.$expanded ? '10px' : '22px'};
    min-width: 240px;

    /* El logo lleva a la home, como en cualquier sitio. */
    cursor: pointer;
    user-select: none;

    &:hover {
        filter: brightness(1.15);
    }
`

const LogoIcon = styled.img`
    width: 32px;
    height: auto;
    filter: drop-shadow(0 0 5px rgba(99, 102, 241, 0.5));
    animation: float 3s ease-in-out infinite;

    @keyframes float {
        0% { transform: translateY(0px); }
        50% { transform: translateY(-3px); }
        100% { transform: translateY(0px); }
    }
`

const DemoSection = styled.div`
    width: 100%;
    min-width: 150px;
    padding: 12px;
    background: rgba(255, 171, 0, 0.1);
    border: 1px solid rgba(255, 171, 0, 0.2);
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 8px;
    transition: background 0.2s ease, border-color 0.2s ease;
    opacity: 0.8;

    /* Only show when expanded */
    display: ${props => props.$expanded ? 'flex' : 'none'};
    animation: fadeIn 0.3s ease;

    &:hover {
        background: rgba(255, 171, 0, 0.16);
        border-color: rgba(255, 171, 0, 0.4);
        opacity: 0.9;
    }

    @keyframes fadeIn {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
    }
`

const DemoTitle = styled.div`
    font-family: 'Chakra Petch', sans-serif;
    font-weight: 700;
    font-size: 11px;
    color: #ffab00;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    display: flex;
    align-items: center;
    gap: 6px;

    &::before {
        content: '';
        width: 6px;
        height: 6px;
        background: #ffab00;
        border-radius: 50%;
        box-shadow: 0 0 8px #ffab00;
    }
`

const DemoDesc = styled.div`
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    line-height: 1.4;
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 8px;
`

const PatreonSection = styled.div`
    width: 100%;
    min-width: 150px;
    padding: 12px;
    background: rgba(255, 66, 77, 0.08);
    border: 1px solid rgba(255, 66, 77, 0.25);
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 8px;
    cursor: pointer;
    transition: background 0.2s ease, border-color 0.2s ease;
    opacity: 0.8;

    display: ${props => props.$expanded ? 'flex' : 'none'};
    animation: fadeIn 0.3s ease;

    &:hover {
        background: rgba(255, 66, 77, 0.14);
        border-color: rgba(255, 66, 77, 0.45);
        opacity: 0.9;
    }
`

const PatreonTitle = styled.div`
    font-family: 'Chakra Petch', sans-serif;
    font-weight: 700;
    font-size: 11px;
    color: #ff424d;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    display: flex;
    align-items: center;
    gap: 6px;

    &::before {
        content: '';
        width: 6px;
        height: 6px;
        background: #ff424d;
        border-radius: 50%;
        box-shadow: 0 0 8px #ff424d;
        flex-shrink: 0;
    }
`

const SupportButton = styled.a`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 12px;
    width: 100%;
    padding: 12px;
    border-radius: 10px;
    font-family: 'Chakra Petch', sans-serif;
    font-weight: 700;
    font-size: 12px;
    text-transform: uppercase;
    text-decoration: none;
    color: white;
    cursor: pointer;
    transition: all 0.2s ease;
    border: 1px solid transparent;
    
    &:hover {
        transform: translateY(-1px);
        filter: brightness(1.2);
    }
`

const PatreonButton = styled(SupportButton)`
    background: rgba(255, 66, 77, 0.1); 
    border-color: #ff424d;
    color: #ff424d;
    box-shadow: 0 2px 8px rgba(255, 66, 77, 0.1);

    &:hover {
        background: rgba(255, 66, 77, 0.2); 
    }
`

const SubStarButton = styled(SupportButton)`
    background: rgba(225, 48, 108, 0.1);
    border-color: #e1306c;
    color: #e1306c;
    box-shadow: 0 2px 8px rgba(225, 48, 108, 0.1);

    &:hover {
        background: rgba(225, 48, 108, 0.2);
    }
`

const WizardButton = styled.button`
    width: ${props => props.$expanded ? 'calc(100% - 24px)' : '40px'};
    height: 40px;
    margin: 0 ${props => props.$expanded ? '12px' : '0'};
    margin-bottom: 20px;
    border-radius: 10px;
    border: none;
    background: linear-gradient(135deg, #6366f1 0%, #a855f7 100%);
    color: white;
    font-family: 'Chakra Petch', sans-serif;
    font-weight: 700;
    font-size: 13px;
    text-transform: uppercase;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(168, 85, 247, 0.5);
        filter: brightness(1.1);
    }

    &:active {
        transform: translateY(0);
    }

    ${props => !props.$expanded ? `
        margin-top: 24px;
        margin-bottom: 24px;
    ` : ''}

`

const AppSubtitle = styled.div`
    color: rgba(255, 255, 255, 0.9);
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 2px;
    text-transform: uppercase;
    text-align: center;
    margin-bottom: 30px;
    width: 100%;
    font-family: 'Chakra Petch', sans-serif;
    text-shadow: 0 0 10px rgba(99, 102, 241, 0.4);
    
    display: ${props => props.$expanded ? 'block' : 'none'};
    animation: fadeIn 0.5s ease;
    white-space: nowrap;
`

const MainSideBarContainer = styled.div`
    width: ${props => props.$expanded ? '260px' : '68px'};
    height: 100vh;
    background: rgba(8, 10, 15, 0.4);
    backdrop-filter: blur(20px);
    position: relative;
    border-right: 1px solid rgba(255, 255, 255, 0.03);
    display: flex;
    flex-direction: column;
    align-items: ${props => props.$expanded ? 'flex-start' : 'center'};
    padding: 12px 0;
    padding-bottom: 8px;
    z-index: 100;
    flex-shrink: 0;
    box-shadow: 5px 0 10px rgba(0, 0, 0, 0.05);
    transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    overflow: hidden;
`

const NavItem = styled.div`
    width: ${props => props.$expanded ? 'calc(100% - 24px)' : '40px'};
    height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: ${props => props.$expanded ? 'flex-start' : 'center'};
    margin-bottom: 10px;
    margin-left: ${props => props.$expanded ? '12px' : '0'};
    padding: ${props => props.$expanded ? '0 10px' : '0'};
    cursor: pointer;
    transition: all 0.2s ease;
    position: relative;
    opacity: 0.6;
    color: #fff;
    transition: all 0.6s ease;
    text-shadow: 0 0 6px rgba(0, 0, 0, 1);

    ${props => props.$active && css`
        opacity: 1;
        background: linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(168, 85, 247, 0.2) 100%);
        box-shadow: 0 0 10px rgba(99, 102, 241, 0.3);
        text-shadow: 0 0 2px rgba(188, 246, 255, 1);
        opacity: 1;
    `}

    &:hover {
        opacity: 1;
        background: linear-gradient(135deg, rgba(99, 102, 190, 0.2) 0%, rgba(168, 85, 190, 0.2) 100%);
    }

    /* Sin sesión de Steam el resto de la app no se puede usar (el middleware
       igual rebotaría al login) — los apagamos en vez de dejar que reboten.
       Ojo: NADA de pointer-events:none acá, mata el hover y con él el
       Tooltip que explica por qué está apagado. El click lo corta el onClick. */
    ${props => props.$disabled && css`
        opacity: 0.25;
        cursor: not-allowed;

        &:hover {
            opacity: 0.35;
            background: transparent;
        }
    `}

    svg {
        font-size: 20px;
        min-width: 20px;
    }

    /* Active indicator bar */
    ${props => props.$active && !props.$expanded && css`
        &::after {
            content: '';
            position: absolute;
            left: -14px;
            width: 3px;
            height: 20px;
            background: #6366f1;
            border-radius: 0 4px 4px 0;
            box-shadow: 0 0 8px #6366f199;
        }
    `}
`

const NavText = styled.span`
    margin-left: 14px;
    font-family: 'Chakra Petch', sans-serif;
    font-weight: 500;
    font-size: 13px;
    white-space: normal;
    transition: opacity 0.2s ease;
    letter-spacing: 0.3px;
`

const BottomSection = styled.div`
    margin-top: auto;
    display: flex;
    flex-direction: column;
    align-items: ${props => props.$expanded ? 'flex-start' : 'center'};
    padding: ${props => props.$expanded ? '0 12px' : '0'};
    width: 100%;
    gap: 8px;
`

const MainSideBar_AdSlotWrap = styled.div`
    width: 100%;
`

const LanguageBox = styled.div`
    width: 100%;
    padding: ${props => props.$expanded ? '12px' : '0'};
    background: ${props => props.$expanded ? 'rgba(255, 255, 255, 0.02)' : 'transparent'};
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
    opacity: 1;
    transition: background 0.4s ease, opacity 0.4s ease, box-shadow 0.4s ease;

    user-select: none;

    ${props => !props.$expanded && css`
        padding-top: 10px;
        opacity: 1;
        align-items: center;

    `}

    ${props => props.$expanded && css`
        min-width: 150px;
        padding-right: 30px;
        &:hover {
            opacity: 0.8;
            background: linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(168, 85, 247, 0.2) 100%);
        }
    `}

    ${props => props.$expanded && css`
        //animation: slideIn 0.3s ease-in-out;
        animation-delay: 0.0s;
    `}

    @keyframes slideIn {
        from {
            opacity: 0;
            transform: translateX(-100%);
        }
        to {
            opacity: 1;
            transform: translateX(0);
        }
    }
`

const LanguageHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    color: rgba(255, 255, 255, 0.6);

    svg {
        font-size: 18px;
    }

    span {
        font-family: 'Chakra Petch', sans-serif;
        font-weight: 600;
        font-size: 10px;
        text-transform: uppercase;
        letter-spacing: 0.8px;
    }
`

const CurrentLanguageName = styled.div`
    font-size: 12px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.9);
    padding-left: 28px;
`

const LanguageBadge = styled.div`
    font-size: 12px;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.3);
    text-transform: uppercase;
    text-align: center;
    width: 40px;
    margin-top: 10px;
    margin-bottom: 10px;
    justify-content: center;
    align-items: center;
`

const VersionText = styled.div`
    margin-top: 10px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 10px;
    color: rgba(255, 255, 255, 0.2);
    font-family: 'Inter', sans-serif;
    user-select: none;
    opacity: ${props => props.$expanded ? 1 : 0};
    transition: opacity 0.3s ease;
    white-space: nowrap;
    pointer-events: none;
    width: 100%;
    text-align: center;
`

// Track previous state across remounts (client-side navigation)
let previousExpandedState = null;

const AccountAvatar = styled.img`
    width: 22px;
    height: 22px;
    min-width: 22px;
    border-radius: 4px;
    border: 1px solid rgba(99, 102, 241, 0.5);
    object-fit: cover;
`

const AccountName = styled(NavText)`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`

const MainSideBar = ({ activeTab, onTabChange, collapsedTabs = ['editor'], onPrefetch }) => {
    const { t, language, setLanguage } = useLanguage()
    const { hasFeature } = usePro()
    // El banner de apoyo es publicidad: el que ya paga no lo ve.
    const muestraPromos = hasFeature('publicidad')
    const { user, isAuthenticated, loading: authLoading } = useAuth()

    // Mientras AuthContext resuelve /api/auth/me no apagamos nada: si no,
    // el sidebar parpadea en gris en cada carga de un usuario logueado.
    const navLocked = !authLoading && !isAuthenticated

    const shouldBeExpanded = !collapsedTabs.includes(activeTab);

    // Initialize state:
    // If we have history (navigation), start with PREVIOUS state to allow animation to new state.
    // If no history (first load), start with DESIRED state to avoid flickering.
    const [expanded, setExpanded] = useState(() => {
        if (previousExpandedState !== null) {
            return previousExpandedState;
        }
        return shouldBeExpanded;
    })


    // Auto-expand/collapse effect based on active tab
    React.useEffect(() => {
        if (expanded !== shouldBeExpanded) {
            setExpanded(shouldBeExpanded);
        }
        // Save state for next transition
        previousExpandedState = shouldBeExpanded;
    }, [shouldBeExpanded, expanded])

    const navItems = [
        // La home es pública: NO se bloquea sin sesión (`alwaysOn`), es lo
        // primero que ve un visitante y tiene que poder volver ahí siempre.
        { id: 'home', icon: <HomeIcon />, label: t('SIDEBAR_HOME'), alwaysOn: true },
        { id: 'manager', icon: <ManagerIcon />, label: t('SIDEBAR_MANAGER') },
        { id: 'editor', icon: <EditorIcon />, label: t('SIDEBAR_EDITOR') },
        // { id: 'wiki', icon: <WikiIcon />, label: t('SIDEBAR_WIKI') },
        { id: 'blog', icon: <BlogIcon />, label: t('SIDEBAR_BLOG') },
        // { id: 'updates', icon: <UpdatesIcon />, label: t('SIDEBAR_UPDATES') },
    ]

    const cycleLanguage = () => {
        if (language === 'en') setLanguage('es')
        else if (language === 'es') setLanguage('pt')
        else setLanguage('en')
    }

    const languageNames = {
        en: 'English / US',
        es: 'Español / ARG',
        pt: 'Português / BR'
    }

    return (
        <MainSideBarContainer $expanded={expanded}>
            <LogoSection
                $expanded={expanded}
                onClick={() => onTabChange('home')}
                role="link"
                tabIndex={0}
                aria-label={t('SIDEBAR_HOME')}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onTabChange('home') } }}
            >
                {expanded ? (
                    <AnimatedLogo />
                ) : (
                    <LogoIcon src="/icon-cubic.png" alt="CS" />
                )}
            </LogoSection>

            <AppSubtitle $expanded={expanded}>
                CS2 CONFIG MANAGER
            </AppSubtitle>

            {/* <Tooltip title={!expanded ? t('START_MAGIC') || "Wizard" : ""} placement="right">
                <WizardButton $expanded={expanded} onClick={() => console.log('Wizard')}>
                    {!expanded ? <AutoFixHigh style={{ fontSize: 20 }} /> : (t('START_MAGIC') || "MAGIC SETUP")}
                </WizardButton>
            </Tooltip> */}

            {navItems.map((item) => (
                <Tooltip
                    key={item.id}
                    title={(navLocked && !item.alwaysOn) ? t('SIDEBAR_LOGIN_REQUIRED') : (!expanded ? item.label : "")}
                    placement="right"
                >
                    <NavItem
                        $active={activeTab === item.id}
                        $expanded={expanded}
                        $disabled={navLocked && !item.alwaysOn}
                        onClick={() => { if (!navLocked || item.alwaysOn) onTabChange(item.id) }}
                        onMouseEnter={() => {
                            if (onPrefetch && (!navLocked || item.alwaysOn)) {
                                // Map sidebar IDs to prefetch keys if necessary
                                const map = {
                                    'dashboard': 'home', // 'dashboard' maps to 'home' view
                                    'manager': 'manager',
                                    'editor': 'crosshair', // Default editor view
                                    'wiki': 'wiki',
                                    'blog': 'blog',
                                    'updates': 'updates'
                                };
                                const target = map[item.id] || item.id;
                                onPrefetch(target);
                            }
                        }}
                    >
                        {item.icon}
                        {expanded && <NavText $show={expanded}>{item.label}</NavText>}
                    </NavItem>
                </Tooltip>
            ))}


            <BottomSection $expanded={expanded}>
                {muestraPromos && (
                    <DemoSection $expanded={expanded} style={{ cursor: 'pointer' }} onClick={() => getBridge()?.openExternal('https://www.patreon.com/ConfigStrike')}>
                        <DemoTitle>{t('SUPPORT_BANNER_TITLE')}</DemoTitle>
                        <DemoDesc>{t('SUPPORT_BANNER_DESC')}</DemoDesc>
                    </DemoSection>
                )}
                <PatreonSection $expanded={expanded} style={{ cursor: 'pointer' }} onClick={() => getBridge()?.openExternal('https://www.patreon.com/ConfigStrike')}>
                    <PatreonTitle>{t('PATREON_BANNER_TITLE')}</PatreonTitle>
                    <DemoDesc>{t('PATREON_BANNER_DESC')}</DemoDesc>
                </PatreonSection>
                {/* Slot publicitario (template) — solo con el sidebar expandido, el
                    placeholder no entra en el riel colapsado de 40px. */}
                {expanded && (
                    <MainSideBar_AdSlotWrap>
                        <AdSlot slot="sidebar-left" />
                    </MainSideBar_AdSlotWrap>
                )}
                {/* Cuenta: sin sesión es el botón de login; con sesión muestra
                    el avatar y el nombre de Steam y abre la vista de cuenta.
                    Nunca se deshabilita — es la única salida cuando navLocked. */}
                <Tooltip
                    title={isAuthenticated ? (user?.displayName || user?.username || '') : t('SIDEBAR_LOGIN_REQUIRED')}
                    placement="right"
                >
                    <NavItem
                        $active={activeTab === 'account'}
                        $expanded={expanded}
                        onClick={() => onTabChange('account')}
                        style={{ marginBottom: 4, marginLeft: 0, width: expanded ? '100% ' : '40px' }}
                    >
                        {isAuthenticated && user?.avatarUrl ? (
                            <AccountAvatar src={user.avatarUrl} alt="" />
                        ) : (
                            <LoginIcon />
                        )}
                        {expanded && (
                            <AccountName>
                                {isAuthenticated
                                    ? (user?.displayName || user?.username)
                                    : t('SIDEBAR_LOGIN')}
                            </AccountName>
                        )}
                    </NavItem>
                </Tooltip>
                <Tooltip
                    title={navLocked ? t('SIDEBAR_LOGIN_REQUIRED') : (!expanded ? (t('SETTINGS') || 'Settings') : "")}
                    placement="right"
                >
                    <NavItem
                        $active={activeTab === 'settings'}
                        $expanded={expanded}
                        $disabled={navLocked}
                        onClick={() => { if (!navLocked) onTabChange('settings') }}
                        style={{ marginBottom: 4, marginLeft: 0, width: expanded ? '100% ' : '40px' }}
                    >
                        <SettingsIcon />
                        {expanded && <NavText>{t('SETTINGS') || 'Settings'}</NavText>}
                    </NavItem>
                </Tooltip>
                <Tooltip title={!expanded ? languageNames[language] : ""} placement="right">
                    <LanguageBox $expanded={expanded} onClick={cycleLanguage}>
                        {expanded ? (
                            <>
                                <LanguageHeader>
                                    <LanguageIcon />
                                    <span>{t('LANGUAGE') || 'Language'}</span>
                                </LanguageHeader>
                                <CurrentLanguageName>
                                    {languageNames[language]}
                                </CurrentLanguageName>
                            </>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', justifyContent: 'center' }}>
                                <LanguageIcon style={{ fontSize: '20px' }} />
                                <LanguageBadge>
                                    {language === 'en' ? 'US' : language === 'pt' ? 'BR' : language.toUpperCase()}
                                </LanguageBadge>
                            </div>
                        )}
                    </LanguageBox>
                </Tooltip>

            </BottomSection>

        </MainSideBarContainer >
    )
}

export default MainSideBar
